import React, { useState } from 'react';
import { Plus, Search, Edit2, Trash2, Building2, FileText, MapPin, Copy, Download, DollarSign, X, Phone, Users, Upload, ImageOff } from 'lucide-react';
import { apiService } from '../services/api';
import { Company } from '../types';
import RoleBasedComponent from './RoleBasedComponent';
import { useAuth } from '../contexts/AuthContext';
import { showGlobalNotification } from '../hooks/useNotifications';
import { useGlobalSearch } from '../hooks/useGlobalSearch';
import Modal from './Modal';
import { useLanguage } from '../contexts/LanguageContext';

const CompaniesPage: React.FC = () => {
  const { hasPermission, user } = useAuth();
  const { t, language } = useLanguage();
  const [searchTerm, setSearchTerm] = useState('');
  useGlobalSearch('companies', setSearchTerm);
  const [showModal, setShowModal] = useState(false);
  const [editingCompany, setEditingCompany] = useState<Company | null>(null);
  const [companiesData, setCompaniesData] = useState<Company[]>([]);
  const [locationsData, setLocationsData] = useState<any[]>([]);
  const [gsmLinesData, setGsmLinesData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [companyData, setCompanyData] = useState({
    name: '',
    tax_number: '',
    tax_office: '',
    location_id: 0
  });
  const [showCapexModal, setShowCapexModal] = useState(false);
  const [selectedCompanyCapex, setSelectedCompanyCapex] = useState<any[]>([]);
  const [selectedCompanyName, setSelectedCompanyName] = useState('');
  const [logoUploading, setLogoUploading] = useState(false);

  // Load data on component mount
  React.useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Fetch companies
      const companiesData = await apiService.getCompanies();
      
      // Fetch locations
      const locationsData = await apiService.getLocations();
      
      // Fetch GSM lines
      const gsmLinesData = await apiService.getGSMLines();
      
      setCompaniesData(Array.isArray(companiesData) ? companiesData : []);
      setLocationsData(Array.isArray(locationsData) ? locationsData : []);
      setGsmLinesData(Array.isArray(gsmLinesData) ? gsmLinesData : []);
    } catch (error) {
      console.error('Error loading data:', error);
      showGlobalNotification.error(language === 'en' ? 'Error' : 'Hata', t('companies.errLoad'));
    } finally {
      setLoading(false);
    }
  };

  const filteredCompanies = Array.isArray(companiesData) ? companiesData.filter(company =>
    (company.name && String(company.name || '').toLowerCase().includes(searchTerm.toLowerCase())) ||
    (company.tax_number && company.tax_number.includes(searchTerm))
  ) : [];

  const logActivity = (action: string, description: string, companyName?: string) => {
    // Activity logging is now handled by the backend
  };

  const handleEdit = (company: Company) => {
    setEditingCompany(company);
    setCompanyData({
      name: company.name,
      tax_number: company.tax_number,
      tax_office: company.tax_office || '',
      location_id: company.location_id
    });
    setShowModal(true);
  };

  const handleAdd = () => {
    setEditingCompany(null);
    setCompanyData({
      name: '',
      tax_number: '',
      tax_office: '',
      location_id: 0
    });
    setShowModal(true);
  };

  const handleCopy = (company: Company) => {
    const textToCopy = `${company.name}\n${company.tax_office || 'Vergi Dairesi Yok'}\n${company.tax_number}`;
    navigator.clipboard.writeText(textToCopy).then(() => {
      showGlobalNotification.success(language === 'en' ? 'Success' : 'Başarılı', t('companies.successCopied'));
    }).catch(() => {
      showGlobalNotification.error(language === 'en' ? 'Error' : 'Hata', t('companies.errCopy'));
    });
  };

  const handleSave = async () => {
    if (!companyData.name || !companyData.tax_number) {
      showGlobalNotification.error(language === 'en' ? 'Error' : 'Hata', t('companies.errRequired'));
      return;
    }

    try {
      if (editingCompany) {
        await apiService.updateCompany(editingCompany.id, companyData);
        showGlobalNotification.success(language === 'en' ? 'Success' : 'Başarılı', t('companies.successUpdated'));
      } else {
        await apiService.createCompany(companyData);
        showGlobalNotification.success(language === 'en' ? 'Success' : 'Başarılı', t('companies.successCreated'));
      }

      await loadData();
      setShowModal(false);
      setCompanyData({ name: '', tax_number: '', tax_office: '', location_id: 0 });
    } catch (error: any) {
      console.error('Error saving company:', error);
      showGlobalNotification.error(language === 'en' ? 'Error' : 'Hata', error.message || t('companies.errSave'));
    }
  };

  const handleDelete = async (id: number) => {
    const companyToDelete = companiesData.find(c => c.id === id);
    if (!companyToDelete) return;
    
    if (confirm(t('companies.confirmDelete'))) {
      try {
        await apiService.deleteCompany(id);
        await loadData();
      } catch (error: any) {
        console.error('Error deleting company:', error);
        showGlobalNotification.error(language === 'en' ? 'Error' : 'Hata', error.message || t('companies.errDelete'));
      }
    }
  };

  const handleLogoUpload = async (companyId: number, file: File) => {
    setLogoUploading(true);
    try {
      const fd = new FormData();
      fd.append('logo', file);
      const resp = await fetch(`/api/companies/${companyId}/logo`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` },
        body: fd
      });
      if (!resp.ok) throw new Error('Logo yüklenemedi');
      const data = await resp.json();
      await loadData();
      setEditingCompany(prev => prev ? { ...prev, logo_url: data.logo_url } : prev);
      showGlobalNotification.success(language === 'en' ? 'Success' : 'Başarılı', 'Logo yüklendi');
    } catch {
      showGlobalNotification.error(language === 'en' ? 'Error' : 'Hata', 'Logo yüklenirken hata oluştu');
    } finally {
      setLogoUploading(false);
    }
  };

  const handleLogoDelete = async (companyId: number) => {
    try {
      const resp = await fetch(`/api/companies/${companyId}/logo`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });
      if (!resp.ok) throw new Error('Logo silinemedi');
      await loadData();
      setEditingCompany(prev => prev ? { ...prev, logo_url: null } : prev);
      showGlobalNotification.success(language === 'en' ? 'Success' : 'Başarılı', 'Logo silindi');
    } catch {
      showGlobalNotification.error(language === 'en' ? 'Error' : 'Hata', 'Logo silinirken hata oluştu');
    }
  };

  const exportCompanies = () => {
    const csvContent = [
      [language === 'en' ? 'Company Name' : 'Şirket Adı', language === 'en' ? 'Tax Number' : 'Vergi Numarası', language === 'en' ? 'Tax Office' : 'Vergi Dairesi', language === 'en' ? 'Location' : 'Lokasyon', language === 'en' ? 'Created Date' : 'Oluşturulma Tarihi'],
      ...filteredCompanies.map(company => [
        company.name,
        company.tax_number,
        company.tax_office || '',
        company.location?.name || '',
        new Date(company.created_at).toLocaleDateString(language === 'en' ? 'en-US' : 'tr-TR')
      ])
    ].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `companies_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    showGlobalNotification.success(language === 'en' ? 'Success' : 'Başarılı', t('companies.successExport'));
  };

  const showCompanyCapex = async (company: Company) => {
    try {
      const capexData = await apiService.getCapex({ company_id: company.id });
      setSelectedCompanyCapex(Array.isArray(capexData) ? capexData : []);
      setSelectedCompanyName(company.name);
      setShowCapexModal(true);
    } catch (error: any) {
      console.error('Error fetching CAPEX:', error);
      showGlobalNotification.error(language === 'en' ? 'Error' : 'Hata', error.message || t('companies.errCapex'));
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">{t('companies.title')}</h1>
          <p className="text-gray-600 dark:text-gray-400">{t('companies.subtitle')}</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={exportCompanies}
            className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors mt-4 sm:mt-0"
          >
            <Download className="w-4 h-4 mr-2" />
            {t('companies.export')}
          </button>
          <RoleBasedComponent requiredPermissions={['companies.create']}>
            <button
              onClick={handleAdd}
              className="flex items-center px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors mt-4 sm:mt-0"
            >
              <Plus className="w-4 h-4 mr-2" />
              {t('companies.newCompany')}
            </button>
          </RoleBasedComponent>
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
        <input
          type="text"
          placeholder={t('companies.searchPlaceholder')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent w-full sm:w-96"
        />
      </div>

      {/* Companies Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {Array.isArray(filteredCompanies) && filteredCompanies.map((company) => (
          <div
            key={company.id}
            className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 hover:shadow-md transition-shadow"
          >
            <div className="flex justify-between items-start mb-4">
              <div className="flex items-center">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0">
                  {company.logo_url ? (
                    <img src={company.logo_url} alt={company.name} className="w-full h-full object-contain p-1" />
                  ) : (
                    <Building2 className="w-5 h-5 text-green-600" />
                  )}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 ml-3">
                  {company.name}
                </h3>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => handleCopy(company)}
                  className="p-1 text-gray-400 hover:text-green-600 transition-colors"
                  title="Kopyala"
                >
                  <Copy className="w-4 h-4" />
                </button>
                <RoleBasedComponent requiredPermissions={['companies.update']}>
                  <button
                    onClick={() => handleEdit(company)}
                    className="p-1 text-gray-400 hover:text-primary transition-colors"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                </RoleBasedComponent>
                <RoleBasedComponent requiredPermissions={['companies.delete']}>
                  <button
                    onClick={() => handleDelete(company.id)}
                    className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </RoleBasedComponent>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center">
                <FileText className="w-4 h-4 text-gray-400 mr-2" />
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  <p>{t('companies.taxNo')} {company.tax_number}</p>
                  {company.tax_office && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{t('companies.taxOffice')} {company.tax_office}</p>
                  )}
                </div>
              </div>
              
              {company.location && (
                <div className="flex items-center">
                  <MapPin className="w-4 h-4 text-gray-400 mr-2" />
                  <p className="text-sm text-gray-600 dark:text-gray-400">{company.location.name}</p>
                </div>
              )}
              
              <div className="flex items-center">
                <Phone className="w-4 h-4 text-gray-400 mr-2" />
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {gsmLinesData.filter(gsm => gsm.company_id === company.id).length} {t('companies.gsmLines')}
                </p>
              </div>
              
              <div className="flex items-center space-x-4">
                <div className="flex items-center">
                  <Users className="w-4 h-4 text-gray-400 mr-2" />
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {company.user_count || 0} {t('companies.users')}
                  </p>
                </div>
                <div className="flex items-center">
                  <FileText className="w-4 h-4 text-gray-400 mr-2" />
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {company.printer_count || 0} {t('companies.printers')}
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-4 pt-4 border-t border-gray-100 flex justify-between items-center">
              <p className="text-xs text-gray-400">
                {t('companies.createdAt')} {new Date(company.created_at).toLocaleDateString(language === 'en' ? 'en-US' : 'tr-TR')}
              </p>
              <button
                onClick={() => showCompanyCapex(company)}
                className="flex items-center px-2 py-1 text-xs bg-primary/20 text-primary-dark rounded hover:bg-primary/30 transition-colors"
                title="CAPEX Görüntüle"
              >
                <DollarSign className="w-3 h-3 mr-1" />
                CAPEX
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Modal */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-md max-h-[85vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {editingCompany ? t('companies.editTitle') : t('companies.newTitle')}
              </h2>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('companies.labelName')}
                </label>
                <input
                  type="text"
                  value={companyData.name}
                  onChange={(e) => setCompanyData({...companyData, name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="Örn: TechCorp A.Ş."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('companies.labelTaxNo')}
                </label>
                <input
                  type="text"
                  value={companyData.tax_number}
                  onChange={(e) => setCompanyData({...companyData, tax_number: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="1234567890"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('companies.labelTaxOffice')}
                </label>
                <input
                  type="text"
                  value={companyData.tax_office}
                  onChange={(e) => setCompanyData({...companyData, tax_office: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="Örn: Kadıköy Vergi Dairesi"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('companies.labelLocation')}
                </label>
                <select
                  value={companyData.location_id}
                  onChange={(e) => setCompanyData({...companyData, location_id: parseInt(e.target.value)})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  <option value={0}>{t('companies.selectLocation')}</option>
                  {Array.isArray(locationsData) && locationsData.map((location) => (
                    <option key={location.id} value={location.id}>
                      {location.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Logo Yükleme - Sadece düzenleme modunda */}
              {editingCompany && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Şirket Logosu
                  </label>
                  <div className="flex items-center gap-3">
                    {editingCompany.logo_url ? (
                      <>
                        <div className="w-16 h-16 border border-gray-200 dark:border-gray-600 rounded-lg flex items-center justify-center overflow-hidden bg-gray-50 dark:bg-gray-700">
                          <img src={editingCompany.logo_url} alt="Logo" className="w-full h-full object-contain p-1" />
                        </div>
                        <div className="flex flex-col gap-2">
                          <label className={`flex items-center gap-1.5 px-3 py-1.5 text-sm bg-primary text-white rounded-md cursor-pointer hover:bg-primary-dark transition-colors ${logoUploading ? 'opacity-50 pointer-events-none' : ''}`}>
                            <Upload className="w-3.5 h-3.5" />
                            Değiştir
                            <input type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) handleLogoUpload(editingCompany.id, f); e.target.value = ''; }} />
                          </label>
                          <button
                            type="button"
                            onClick={() => handleLogoDelete(editingCompany.id)}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-red-300 text-red-600 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                          >
                            <ImageOff className="w-3.5 h-3.5" />
                            Sil
                          </button>
                        </div>
                      </>
                    ) : (
                      <label className={`flex items-center gap-2 px-4 py-2 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer hover:border-primary hover:bg-primary/5 transition-colors ${logoUploading ? 'opacity-50 pointer-events-none' : ''}`}>
                        <Upload className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-500 dark:text-gray-400">
                          {logoUploading ? 'Yükleniyor...' : 'Logo yükle (PNG, JPG, SVG – maks. 2MB)'}
                        </span>
                        <input type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) handleLogoUpload(editingCompany.id, f); e.target.value = ''; }} />
                      </label>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-3">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700 transition-colors"
              >
                {t('companies.cancel')}
              </button>
              <button
                onClick={handleSave}
                className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark transition-colors"
              >
                {editingCompany ? t('companies.update') : t('companies.save')}
              </button>
            </div>
          </div>
        </Modal>

      {/* CAPEX Modal */}
      <Modal isOpen={showCapexModal} onClose={() => setShowCapexModal(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {selectedCompanyName} - {t('companies.capexList')}
                </h2>
                <button
                  onClick={() => setShowCapexModal(false)}
                  className="p-2 text-gray-400 hover:text-gray-600 dark:text-gray-400 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 dark:bg-gray-700 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="p-6">
              {selectedCompanyCapex.length === 0 ? (
                <div className="text-center py-8">
                  <DollarSign className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500 dark:text-gray-400">{t('companies.noCapex')}</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          {t('companies.capexProjectName')}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          {t('companies.capexCategory')}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          {t('companies.capexAmount')}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          {t('companies.capexStatus')}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          {t('companies.capexDate')}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {Array.isArray(selectedCompanyCapex) && selectedCompanyCapex.map((capex) => (
                        <tr key={capex.id}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">
                            {capex.project_name}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                            {capex.category}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                            {new Intl.NumberFormat('tr-TR', {
                              style: 'currency',
                              currency: capex.currency || 'TRY'
                            }).format(capex.amount)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              capex.status === 'approved' ? 'bg-green-100 text-green-800' :
                              capex.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                              capex.status === 'rejected' ? 'bg-red-100 text-red-800' :
                              'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
                            }`}>
                              {capex.status === 'approved' ? t('companies.capexApproved') :
                               capex.status === 'pending' ? t('companies.capexPending') :
                               capex.status === 'rejected' ? t('companies.capexRejected') : capex.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                            {new Date(capex.created_at).toLocaleDateString(language === 'en' ? 'en-US' : 'tr-TR')}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </Modal>
    </div>
  );
};

export default CompaniesPage;